import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { Radar, Lightbulb, Target, Wand2, SlidersHorizontal, FileText, Store, ScrollText, Rocket, Briefcase, BookOpen, ShieldCheck, Crosshair, Search, Sparkles, ChevronRight, RefreshCw, Terminal, Menu, X } from "lucide-react";
import SearchModal from "./SearchModal";
import CopilotPanel from "./CopilotPanel";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { LogOut, Users } from "lucide-react";
import { ThemeToggle } from "./ui/ThemeToggle";

function relTime(iso) {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function CrawlBadge() {
  const [lastCrawl, setLastCrawl] = useState(null);
  const [crawling, setCrawling] = useState(false);
  const [, setTick] = useState(0);

  const load = () => api.ingestRuns().then((d) => {
    const t = d.runs?.[0]?.finished_at;
    if (t) setLastCrawl(t);
  }).catch(() => {});

  useEffect(() => {
    load();
    const iv = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(iv);
  }, []);

  const recrawl = async () => {
    if (crawling) return;
    setCrawling(true);
    const baseline = lastCrawl;
    try {
      await api.runIngest(); // returns immediately; crawl runs in background
      // Poll for completion (timestamp advances past baseline), up to ~2.5 min
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const d = await api.ingestRuns().catch(() => null);
        const t = d?.runs?.[0]?.finished_at;
        if (t && t !== baseline) { setLastCrawl(t); break; }
      }
    } finally {
      setCrawling(false);
    }
  };

  return (
    <button
      data-testid="last-crawl-badge"
      onClick={recrawl}
      disabled={crawling}
      title={crawling ? "Crawling all data sources…" : "Click to re-crawl all data sources"}
      className={`flex items-center gap-2 px-3 h-8 text-xs font-mono border transition-colors ${
        crawling
          ? "bg-bm-elevated border-bm-amber/60 text-bm-amber"
          : "bg-bm-elevated border-white/10 text-bm-muted hover:border-bm-green/60 hover:text-bm-green"
      }`}
    >
      <RefreshCw className={`w-3.5 h-3.5 ${crawling ? "animate-spin" : ""}`} />
      <span>{crawling ? "CRAWLING…" : `CRAWL ${relTime(lastCrawl)}`}</span>
    </button>
  );
}

const navGroups = [
  {
    label: "DISCOVER",
    items: [
      { to: "/logs", label: "Logs", icon: Terminal, key: "logs" },
      { to: "/signals", label: "Signals", icon: Radar, key: "signals" },
      { to: "/insights", label: "Insights", icon: Lightbulb, key: "insights" },
      { to: "/opportunities", label: "Opportunities", icon: Target, key: "opps" },
    ],
  },
  {
    label: "VALIDATE",
    items: [
      { to: "/validation", label: "Validation", icon: ShieldCheck, key: "validation" },
      { to: "/whitespace", label: "Whitespace", icon: Crosshair, key: "whitespace" },
    ],
  },
  {
    label: "CREATE",
    items: [
      { to: "/concepts", label: "Concepts", icon: Wand2, key: "concepts" },
      { to: "/simulate", label: "Simulators", icon: SlidersHorizontal, key: "sim" },
      { to: "/briefs", label: "Briefs", icon: FileText, key: "briefs" },
    ],
  },
  {
    label: "SOURCE",
    items: [
      { to: "/suppliers", label: "Suppliers", icon: Store, key: "suppliers" },
      { to: "/rfps", label: "RFPs", icon: ScrollText, key: "rfps" },
    ],
  },
  {
    label: "LAUNCH",
    items: [
      { to: "/launches", label: "Launch Intel", icon: Rocket, key: "launch" },
    ],
  },
  {
    label: "SCALE",
    items: [
      { to: "/projects", label: "Projects", icon: Briefcase, key: "projects" },
    ],
  },
  {
    label: "KNOWLEDGE",
    items: [
      { to: "/learnings", label: "Learnings", icon: BookOpen, key: "learnings" },
    ],
  },
];

// Flat list for breadcrumb lookup
const allItems = navGroups.flatMap((g) => g.items);

export default function Layout({ children }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setCopilotOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const crumb = allItems.find(n => n.to === location.pathname)?.label
    || (location.pathname === "/" ? "Intelligence Feed"
      : location.pathname === "/team" ? "Team Access" : "Detail");

  return (
    <div className="min-h-screen flex bg-bm-bg text-bm-text font-body relative z-[2]">
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-overlay"
          className="fixed inset-0 z-40 bg-black/70 md:hidden"
        />
      )}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-white/10 bg-bm-surface backdrop-blur-sm flex flex-col transform transition-transform duration-200 md:static md:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-close"
          className="absolute right-3 top-3 p-2 text-bm-muted hover:text-white md:hidden"
        >
          <X className="w-4 h-4" />
        </button>
        <Link to="/" className="block p-5 border-b border-white/10 hover:bg-white/[0.02] transition-colors" data-testid="brand-link">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-bm-green animate-pulse"></div>
            <span className="label-mono" data-testid="brand-label">MEOLAA / PROJECT</span>
          </div>
          <div className="font-heading text-2xl font-black tracking-tight mt-1" data-testid="brand-title">
            BLACK MIRROR
          </div>
          <div className="label-mono mt-1 text-bm-amber">CONSUMER PREDICTION OS</div>
        </Link>

        <nav className="flex-1 py-4 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-4">
              <div className="px-5 pb-2 pt-1 label-mono text-bm-dim tracking-[0.18em]" data-testid={`nav-group-${group.label.toLowerCase()}`}>
                {group.label}
              </div>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    data-testid={`nav-${item.key}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${
                        isActive
                          ? "border-bm-amber bg-white/[0.03] text-white"
                          : "border-transparent text-bm-muted hover:text-white hover:bg-white/[0.02]"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
          {isAdmin && (
            <div className="mb-4">
              <div className="px-5 pb-2 pt-1 label-mono text-bm-dim tracking-[0.18em]" data-testid="nav-group-admin">ADMIN</div>
              <NavLink to="/team" data-testid="nav-team"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive ? "border-bm-amber bg-white/[0.03] text-white"
                      : "border-transparent text-bm-muted hover:text-white hover:bg-white/[0.02]"}`}>
                <Users className="w-4 h-4 shrink-0" />
                <span className="truncate">Team Access</span>
              </NavLink>
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between gap-2 mb-3" data-testid="user-chip">
            <div className="min-w-0">
              <div className="text-xs font-mono text-white truncate">{user?.email}</div>
              <div className="label-mono text-bm-dim uppercase">{user?.role}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" title="Sign out"
              className="p-1.5 border border-white/10 text-bm-dim hover:border-bm-danger hover:text-bm-danger transition-colors shrink-0">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="label-mono text-bm-dim mb-2">SYSTEM STATUS</div>
          <div className="flex items-center justify-between text-xs font-mono">
            <span className="text-bm-muted">Ingest</span>
            <span className="text-bm-green flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-bm-green rounded-full animate-pulse"></span>LIVE
            </span>
          </div>
          <div className="flex items-center justify-between text-xs font-mono mt-1">
            <span className="text-bm-muted">Claude 4.5</span>
            <span className="text-bm-cyan">READY</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="border-b border-white/10 bg-bm-bg/80 backdrop-blur-md sticky top-0 z-30">
          <div className="flex items-center justify-between px-3 sm:px-6 h-14 gap-2">
            <div className="flex items-center gap-2 sm:gap-3 text-sm min-w-0">
              <button
                type="button"
                aria-label="Open navigation"
                onClick={() => setMobileNavOpen(true)}
                data-testid="mobile-nav-toggle"
                className="p-2 -ml-2 text-bm-muted hover:text-white md:hidden shrink-0"
              >
                <Menu className="w-4 h-4" />
              </button>
              <span className="label-mono text-bm-dim">PATH /</span>
              <span className="font-mono text-bm-text truncate">{crumb}</span>
              <ChevronRight className="w-3 h-3 text-bm-dim hidden sm:block" />
              <span className="font-mono text-bm-amber blink-cursor hidden sm:block"></span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <ThemeToggle testId="topbar-theme-toggle" />
              <div className="hidden xl:block"><CrawlBadge /></div>
              <button
                data-testid="open-search-btn"
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-3 h-8 text-xs font-mono bg-bm-elevated border border-white/10 hover:border-bm-cyan/60 hover:text-bm-cyan transition-colors"
              >
                <Search className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">Search intelligence...</span>
                <span className="ml-4 text-bm-dim hidden xl:inline">⌘K</span>
              </button>
              <button
                data-testid="open-copilot-btn"
                onClick={() => setCopilotOpen(true)}
                className="flex items-center gap-2 px-3 h-8 text-xs font-mono bg-bm-amber text-black hover:bg-white transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">COPILOT</span>
                <span className="ml-2 opacity-70 hidden lg:inline">⌘J</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-3 sm:p-6 min-w-0" data-testid="main-content">
          {children}
        </main>
      </div>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} onNavigate={navigate} />
      <CopilotPanel open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </div>
  );
}
